import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, UserPlus, Check, Sparkles, Loader2,
  Stethoscope, CheckCircle2, X, ShieldCheck, PartyPopper,
  Bell, Users, CalendarClock, HeartPulse, FileText, Download, Circle, BookmarkPlus, Plus,
} from "lucide-react";
import { PageHead } from "@/components/AppLayout";
import { Card, Btn, Badge, Avatar, Spinner, SearchInput } from "@/components/ui-kit";
import { Modal, L } from "@/pages/Protocols";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STEPS = ["Patient", "Diagnosis", "Treatment Plan", "Confirm"];

const ALLERGY_OPTIONS = ["No known drug allergies", "Penicillin", "NSAIDs / Aspirin", "Local anaesthetic", "Latex", "Sulfa drugs", "Iodine / Contrast", "Adhesive tape", "Other (specify)"];

async function downloadOrderDoc(order) {
  try {
    const { data } = await api.get(`/referrals/${order.id}/document.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = `${order.ref_no || "treatment-order"}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Could not download document"); }
}

export default function NewReferral() {
  const nav = useNavigate();
  const { user } = useAuth();
  const draftIdParam = new URLSearchParams(window.location.search).get("draft");
  const [step, setStep] = useState(0);
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [patient, setPatient] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [draftId, setDraftId] = useState(draftIdParam || null);
  const [draftReady, setDraftReady] = useState(!draftIdParam);

  const [dx, setDx] = useState({
    primary_diagnosis: "", procedure: "", body_region: "", affected_side: "N/A",
    severity: "Moderate", recovery_stage: "Acute", surgery_date: "",
    restrictions: [], contraindications: [], clinical_notes: "",
    care_mode: "Home", frequency: "3 times per week", duration: "8 weeks",
    equipment_items: [],
  });

  const [plan, setPlan] = useState(null);
  const [aiSource, setAiSource] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(null);
  const [planConfirmed, setPlanConfirmed] = useState(false);
  const [saveProtoOpen, setSaveProtoOpen] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [protocolId, setProtocolId] = useState(null);
  const [equipmentList, setEquipmentList] = useState([]);

  const loadPatients = () => api.get("/patients").then((r) => setPatients(r.data));
  useEffect(() => { loadPatients(); api.get("/rate-master").then((r) => setEquipmentList(r.data.equipment || [])).catch(() => {}); api.get("/protocols").then((r) => setProtocols(r.data)).catch(() => {}); }, []);

  // Resume a saved draft
  useEffect(() => {
    if (!draftIdParam) return;
    api.get(`/referrals/drafts/${draftIdParam}`).then(async (r) => {
      const d = r.data;
      if (d.diagnosis) setDx((prev) => ({ ...prev, ...d.diagnosis }));
      if (d.plan) setPlan(d.plan);
      if (d.protocol_id) setProtocolId(d.protocol_id);
      setPlanConfirmed(!!d.pathway_confirmed);
      if (d.patient_id) {
        try { const pr = await api.get(`/patients/${d.patient_id}`); setPatient(pr.data); }
        catch { const list = await api.get("/patients"); setPatient((list.data || []).find((p) => p.id === d.patient_id) || null); }
      }
      setStep(d.step || 0);
      toast.info("Resumed your saved draft");
    }).catch(() => toast.error("Could not load draft")).finally(() => setDraftReady(true));
  }, [draftIdParam]);

  // Auto-save draft as the order is built (once a patient is chosen, before it's issued)
  useEffect(() => {
    if (!draftReady || issued || !patient) return;
    const t = setTimeout(() => {
      api.post("/referrals/drafts", {
        id: draftId, step, patient_id: patient.id, patient_name: patient.name,
        diagnosis: dx, plan, protocol_id: protocolId, pathway_confirmed: planConfirmed,
      }).then((r) => { if (!draftId) setDraftId(r.data.id); }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [patient, dx, plan, step, protocolId, planConfirmed, draftReady]); /* eslint-disable-line */

  const saveAndClose = async () => {
    if (!patient) { nav("/referrals"); return; }
    try {
      await api.post("/referrals/drafts", { id: draftId, step, patient_id: patient.id, patient_name: patient.name, diagnosis: dx, plan, protocol_id: protocolId, pathway_confirmed: planConfirmed });
      toast.success("Draft saved — resume it anytime from Treatment Orders");
    } catch { toast.error("Could not save draft"); }
    nav("/referrals");
  };

  const parseWeeks = (d) => parseInt(String(d || "").split(" ")[0], 10) || 0;

  // Any edit to the plan voids the doctor's confirmation — they must re-confirm.
  const editPlan = (next) => { setPlan(next); setPlanConfirmed(false); };

  const updateDx = (patch) => {
    const nd = { ...dx, ...patch };
    setDx(nd);
    if (plan) {
      const next = { ...plan };
      if ("frequency" in patch) next.frequency = nd.frequency;
      if ("duration" in patch) next.weeks = parseWeeks(nd.duration) || plan.weeks;
      editPlan(next);
    }
  };

  const shown = patients.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.phone || "").includes(q) || (p.pranit_id || "").toLowerCase().includes(q.toLowerCase()));

  const REQ = [
    ["Primary Diagnosis", dx.primary_diagnosis], ["Body Region", dx.body_region],
    ["Severity", dx.severity], ["Recovery Stage", dx.recovery_stage],
    ["Care Mode", dx.care_mode], ["Frequency", dx.frequency], ["Duration", dx.duration],
  ];
  const reqDone = REQ.filter(([, v]) => v && String(v).trim()).length;

  const generateAI = async () => {
    setLoadingAI(true); setPlan(null);
    try {
      const { data } = await api.post("/ai/care-pathway", {
        primary_diagnosis: dx.primary_diagnosis, body_region: dx.body_region,
        severity: dx.severity, recovery_stage: dx.recovery_stage, care_mode: dx.care_mode,
        frequency: dx.frequency, duration: dx.duration,
      });
      setPlan(data.plan); setPlanConfirmed(false); setAiSource(data.source);
      toast.success(data.source === "ai" ? "AI treatment plan generated" : "Treatment plan ready");
    } catch (e) { toast.error("Could not generate plan"); }
    setLoadingAI(false);
  };

  const goStep = (n) => {
    if (n === 1 && !patient) return toast.error("Select or create a patient first");
    if (n === 2 && !dx.primary_diagnosis.trim()) return toast.error("Enter a primary diagnosis");
    if (n === 2 && !plan) generateAI();
    if (n === 3 && !planConfirmed) return toast.error("Review the plan and confirm it as your treatment pathway to continue");
    setStep(n);
  };

  const submit = async () => {
    if (!plan) return toast.error("Generate a treatment plan first");
    if (!planConfirmed) return toast.error("Confirm the pathway as your own before issuing the order");
    setSaving(true);
    try {
      const { data } = await api.post("/referrals", {
        patient_id: patient.id, diagnosis: dx, selected_package: plan, protocol_id: protocolId, draft_id: draftId, pathway_confirmed: true,
      });
      if (data.patient_invite) toast.success(`Patient portal login created — ${data.patient_invite.email} · temp password: ${data.patient_invite.temp_password}`, { duration: 12000 });
      toast.success(data.approval_status === "Pending" ? "Sent to the doctor for approval" : "Treatment order issued");
      setIssued(data);
    } catch (e) { toast.error("Failed to create treatment order"); setSaving(false); }
  };

  if (issued) return <IssuedScreen order={issued} nav={nav} />;

  if (user?.role === "doctor" && user?.credential_status !== "Verified") return (
    <>
      <PageHead title="New Treatment Order" subtitle="Verification required before issuing orders" />
      <Card className="max-w-xl mx-auto p-8 text-center" data-testid="order-creation-locked">
        <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-4"><ShieldCheck size={26} /></div>
        <h2 className="text-lg font-bold text-slate-900">Documents pending verification</h2>
        <p className="text-[13.5px] text-slate-500 mt-2 leading-relaxed">
          You can browse your dashboard and patients while your documents are pending. Treatment orders unlock once PranIQ Admin verifies your credentials.
        </p>
        <div className="flex justify-center gap-2 mt-5">
          <Btn variant="primary" onClick={() => nav("/doctor/onboarding")} data-testid="locked-upload-docs-btn">Upload documents</Btn>
          <Btn onClick={() => nav("/dashboard")} data-testid="locked-back-dashboard-btn">Back to dashboard</Btn>
        </div>
      </Card>
    </>
  );

  return (
    <>
      <PageHead title="New Treatment Order" subtitle="Search a patient, prescribe a plan, and send an AI-recommended care pathway."
        action={<div className="flex gap-2">
          <Btn onClick={saveAndClose} data-testid="save-draft-close"><FileText size={16} /> Save &amp; close</Btn>
          <Btn onClick={() => nav("/referrals")}><ArrowLeft size={16} /> Cancel</Btn>
        </div>} />

      {/* Stepper */}
      <div className="flex items-center mb-7 max-w-2xl">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold transition-all ${
                i < step ? "bg-teal-600 text-white" : i === step ? "bg-white border-[3px] border-teal-600 text-teal-600" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <Check size={16} /> : i + 1}
              </div>
              <span className={`text-[11.5px] font-semibold ${i <= step ? "text-slate-700" : "text-slate-400"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 mb-5 ${i < step ? "bg-teal-600" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      {/* STEP 1 — Patient */}
      {step === 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-5 items-start animate-fade-in">
          <Card>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div><h3 className="font-display font-semibold text-[15px]">Search Patient</h3><p className="text-[12.5px] text-slate-500">By name, phone, or PranIQ ID</p></div>
              <Btn variant="primary" data-testid="create-patient-button" onClick={() => setShowCreate(true)}><UserPlus size={15} /> Create New Patient</Btn>
            </div>
            <div className="p-4">
              <SearchInput testid="patient-search-input" value={q} onChange={setQ} placeholder="Search by name, phone, PranIQ ID…" className="mb-2" />
            </div>
            {shown.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-slate-500">
                No patients found. <button className="text-teal-700 font-semibold" onClick={() => setShowCreate(true)}>Create a new patient</button>.
              </div>
            ) : shown.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 last:border-0" data-testid={`patient-result-${p.id}`}>
                <Avatar name={p.name} size={38} />
                <div className="flex-1"><div className="font-semibold text-[13.5px]">{p.name}</div><div className="text-[11.5px] text-slate-500">{p.age} · {p.gender} · {p.phone} · {p.pranit_id}</div></div>
                <button onClick={() => setPatient(p)} data-testid={`select-patient-${p.id}`}
                  className={`pq-btn pq-btn-sm ${patient?.id === p.id ? "pq-btn-primary" : "border-teal-500 text-teal-700"}`}>
                  {patient?.id === p.id ? "Selected" : "Select"}
                </button>
              </div>
            ))}
          </Card>

          <Card className="p-5" data-testid="selected-patient-card">
            <div className="flex items-center justify-between mb-3"><h3 className="font-display font-semibold text-[15px]">Selected Patient</h3>{patient && <Badge tone="teal">Selected</Badge>}</div>
            {!patient ? <p className="text-sm text-slate-500 py-8 text-center">Select a patient from the list, or create a new one.</p> : (
              <>
                <div className="flex items-center gap-3 mb-3"><Avatar name={patient.name} size={44} /><div><div className="font-display font-bold text-[15px]">{patient.name}</div><div className="text-[12px] text-slate-500">{patient.age} · {patient.gender} · {patient.pranit_id}</div></div></div>
                {[["Phone", patient.phone], ["Email", patient.email], ["Hospital ID", patient.hospital_id], ["Facility", patient.facility], ["Conditions", patient.conditions], ["Allergies", patient.allergies]].map(([k, v]) => (
                  <div key={k} className="flex gap-3 py-2 border-b border-dashed border-slate-100 last:border-0 text-[13px]"><span className="w-24 text-slate-500 shrink-0">{k}</span><span className="font-semibold">{v || "—"}</span></div>
                ))}
              </>
            )}
          </Card>
        </div>
      )}

      {/* STEP 2 — Diagnosis */}
      {step === 1 && (
        <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-5 items-start animate-fade-in">
          <div className="space-y-5">
            <Card className="p-5">
              <h3 className="font-display font-semibold mb-4">Clinical Overview</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <L label="Primary Diagnosis *"><input className="pq-input" data-testid="diagnosis-input" value={dx.primary_diagnosis} onChange={(e) => setDx({ ...dx, primary_diagnosis: e.target.value })} placeholder="e.g. ACL Tear – Grade II" /></L>
                <L label="Body Region *"><input className="pq-input" data-testid="body-region-input" value={dx.body_region} onChange={(e) => setDx({ ...dx, body_region: e.target.value })} placeholder="e.g. Knee" /></L>
              </div>
              <PillGroup label="Affected Side" value={dx.affected_side} options={["Left", "Right", "Bilateral", "N/A"]} onChange={(v) => setDx({ ...dx, affected_side: v })} />
              <PillGroup label="Severity *" value={dx.severity} options={["Mild", "Moderate", "Severe"]} onChange={(v) => setDx({ ...dx, severity: v })} />
              <PillGroup label="Recovery Stage *" value={dx.recovery_stage} options={["Acute", "Sub-acute", "Chronic", "Maintenance"]} onChange={(v) => setDx({ ...dx, recovery_stage: v })} />
            </Card>
            <Card className="p-5">
              <h3 className="font-display font-semibold mb-4">Clinical Considerations</h3>
              <TagInput label="Restrictions" tags={dx.restrictions} onChange={(t) => setDx({ ...dx, restrictions: t })} placeholder="Add a restriction & press Enter" testid="restrictions-input" />
              <TagInput label="Contraindications" tags={dx.contraindications} onChange={(t) => setDx({ ...dx, contraindications: t })} placeholder="Add a contraindication & press Enter" testid="contraindications-input" />
              <L label="Clinical Notes"><textarea className="pq-input min-h-[90px]" data-testid="clinical-notes" value={dx.clinical_notes} onChange={(e) => setDx({ ...dx, clinical_notes: e.target.value })} placeholder="Symptoms, exam findings, response to therapy..." /></L>
            </Card>
            <Card className="p-5">
              <h3 className="font-display font-semibold mb-4">Rehabilitation Preferences</h3>
              <PillGroup label="Care Mode *" value={dx.care_mode} options={["Home", "Virtual", "Hybrid"]} onChange={(v) => setDx({ ...dx, care_mode: v })} />
              <div className="pq-label mb-2">Special Equipment</div>
              <div className="flex flex-wrap gap-2 mb-4" data-testid="equipment-options">
                {equipmentList.length === 0 && <span className="text-[12px] text-slate-400">No equipment configured</span>}
                {equipmentList.map((eq) => {
                  const on = (dx.equipment_items || []).includes(eq.name);
                  return (
                    <button key={eq.name} type="button" data-testid={`equipment-${eq.name.replace(/[^a-z0-9]+/gi, "-")}`}
                      onClick={() => setDx({ ...dx, equipment: true, equipment_items: on ? dx.equipment_items.filter((x) => x !== eq.name) : [...(dx.equipment_items || []), eq.name] })}
                      className={`pq-pill ${on ? "pq-pill-active" : ""}`}>{eq.name}</button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <L label="Frequency (intervals/week) *"><select className="pq-input" data-testid="frequency-select" value={dx.frequency} onChange={(e) => updateDx({ frequency: e.target.value })}><option>1 time per week</option><option>2 times per week</option><option>3 times per week</option><option>4 times per week</option><option>Daily</option><option>Alternate days</option></select></L>
                <L label="Duration (weeks) *"><select className="pq-input" data-testid="duration-select" value={dx.duration} onChange={(e) => updateDx({ duration: e.target.value })}><option>4 weeks</option><option>6 weeks</option><option>8 weeks</option><option>10 weeks</option><option>12 weeks</option><option>16 weeks</option></select></L>
              </div>
            </Card>
          </div>
          <div className="space-y-5 sticky top-24">
            <Card className="p-5" data-testid="prescription-checklist">
              <div className="flex items-center gap-3 mb-3"><Avatar name={patient?.name} size={40} /><div><div className="font-semibold text-[13.5px]">{patient?.name}</div><div className="text-[11.5px] text-slate-500">{patient?.age} · {patient?.gender} · {patient?.pranit_id}</div></div></div>
              <div className="flex items-center justify-between mb-2"><h4 className="font-display font-semibold text-[13px]">Checklist</h4><Badge tone={reqDone === REQ.length ? "emerald" : "amber"}>{reqDone} of {REQ.length} completed</Badge></div>
              {REQ.map(([label, v]) => (
                <div key={label} className="flex items-center gap-2 py-1 text-[12.5px]">
                  {v && String(v).trim() ? <CheckCircle2 size={15} className="text-teal-600" /> : <Circle size={15} className="text-slate-300" />}
                  <span className={v && String(v).trim() ? "text-slate-700" : "text-slate-400"}>{label}</span>
                </div>
              ))}
            </Card>
            <Card className="p-5">
              <div className="flex items-center gap-2 text-indigo-700 font-semibold text-[13px] mb-1"><Sparkles size={15} /> AI Care Pathway</div>
              <p className="text-[12.5px] text-slate-600 leading-relaxed">Once you continue, PranIQ AI analyses this clinical profile and designs an evidence-based treatment plan for the assigned physiotherapist.</p>
            </Card>
          </div>
        </div>
      )}

      {/* STEP 3 — Care Pathway */}
      {step === 2 && (
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <Badge tone="indigo"><Sparkles size={13} /> {aiSource === "ai" ? "AI-suggested" : "Suggested"} pathway for {patient?.name} — editable</Badge>
            <div className="flex gap-2">
              {plan && <Btn data-testid="save-as-protocol-button" onClick={() => setSaveProtoOpen(true)}><BookmarkPlus size={15} /> Save as Protocol</Btn>}
              <Btn data-testid="regenerate-ai-button" onClick={generateAI} disabled={loadingAI}><Sparkles size={15} /> Regenerate</Btn>
            </div>
          </div>
          <div className="text-[11.5px] text-slate-500 -mt-3 mb-4 flex items-center gap-1.5"><ShieldCheck size={13} className="text-teal-600" /> AI only suggests — review and edit every part, then confirm it as your own treatment pathway. The plan is shared only with the assigned physiotherapist and PranIQ, not the patient.</div>
          {loadingAI || !plan ? (
            <Card className="p-14 flex flex-col items-center gap-3"><Loader2 className="w-8 h-8 text-teal-600 animate-spin" /><p className="text-sm text-slate-500">PranIQ AI is designing the treatment plan for {dx.primary_diagnosis}…</p></Card>
          ) : (
            <div className="max-w-2xl">
              <Card className="p-5" data-testid="plan-card">
                <Badge tone="teal">AI-suggested draft — edit to make it yours</Badge>
                <input className="pq-input font-display font-bold text-lg mt-3" value={plan.name || ""} onChange={(e) => editPlan({ ...plan, name: e.target.value })} data-testid="plan-name-input" placeholder="Pathway name" />
                <textarea className="pq-input text-[13px] mt-2 min-h-[64px]" value={plan.description || ""} onChange={(e) => editPlan({ ...plan, description: e.target.value })} data-testid="plan-desc-input" placeholder="Describe the treatment pathway..." />

                {/* Editable schedule — doctor controls intervals & duration per order */}
                <div className="grid grid-cols-2 gap-3 mt-4" data-testid="plan-schedule-editor">
                  <L label="Sessions (total)">
                    <input type="number" min="1" className="pq-input" data-testid="plan-sessions-input" value={plan.sessions}
                      onChange={(e) => editPlan({ ...plan, sessions: Math.max(1, parseInt(e.target.value, 10) || 1) })} />
                  </L>
                  <L label="Duration (weeks)">
                    <input type="number" min="1" className="pq-input" data-testid="plan-weeks-input" value={plan.weeks}
                      onChange={(e) => editPlan({ ...plan, weeks: Math.max(1, parseInt(e.target.value, 10) || 1) })} />
                  </L>
                </div>
                <div className="flex items-center gap-4 mt-1 text-[12.5px] text-slate-600 flex-wrap">
                  <span className="bg-slate-100 rounded-lg px-2.5 py-1">⏱ <b>{plan.frequency || dx.frequency || "—"}</b> per week</span>
                  <span className="bg-slate-100 rounded-lg px-2.5 py-1">🏥 {plan.mode}</span>
                </div>

                <EditableList label="What's included" items={plan.included || []} onChange={(v) => editPlan({ ...plan, included: v })} testid="plan-included" />
                <EditableList label="Milestones" items={plan.milestones || []} onChange={(v) => editPlan({ ...plan, milestones: v })} testid="plan-milestone" />
                <div className={`mt-5 rounded-xl border p-4 flex items-start gap-3 ${planConfirmed ? "border-teal-200 bg-teal-50" : "border-amber-200 bg-amber-50"}`} data-testid="pathway-confirm-block">
                  <input type="checkbox" id="pathway-confirm" className="mt-0.5 w-4 h-4 accent-teal-600 cursor-pointer" checked={planConfirmed} onChange={(e) => setPlanConfirmed(e.target.checked)} data-testid="pathway-confirm-checkbox" />
                  <label htmlFor="pathway-confirm" className="text-[12.5px] leading-relaxed cursor-pointer">
                    <span className={`font-semibold ${planConfirmed ? "text-teal-800" : "text-amber-800"}`}>{planConfirmed ? `Confirmed by ${user?.name || "you"}` : "Confirm as my treatment pathway"}</span>
                    <span className={`block mt-0.5 ${planConfirmed ? "text-teal-700" : "text-amber-700"}`}>{planConfirmed ? "This pathway will be issued in your name." : "I have reviewed this AI-suggested pathway, made my edits, and take clinical responsibility for it. Any further change requires re-confirmation."}</span>
                  </label>
                </div>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* STEP 4 — Confirm */}
      {step === 3 && plan && (
        <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-5 items-start animate-fade-in">
          <Card className="p-6">
            <div className="flex items-center gap-2 text-teal-700 font-display font-semibold text-lg mb-4"><Stethoscope size={20} /> Review & Submit Treatment Order</div>
            <Section title="Patient"><Row k="Name" v={patient.name} /><Row k="Details" v={`${patient.age} · ${patient.gender} · ${patient.pranit_id}`} /></Section>
            <Section title="Diagnosis"><Row k="Primary" v={dx.primary_diagnosis} /><Row k="Body Region" v={dx.body_region} /><Row k="Severity" v={dx.severity} /><Row k="Recovery Stage" v={dx.recovery_stage} /><Row k="Care Mode" v={dx.care_mode} /></Section>
            <Section title="Treatment Plan"><Row k="Plan" v={plan.name} /><Row k="Sessions" v={`${plan.sessions} · ${plan.weeks} weeks`} /><Row k="Frequency" v={plan.frequency || dx.frequency} /></Section>
          </Card>
          <Card className="p-6">
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center mx-auto mb-3"><Stethoscope size={26} /></div>
              <h3 className="font-display font-bold text-lg">Ready to submit</h3>
              <p className="text-sm text-slate-500 mt-1">Submitting opens a case. PranIQ Admin will assign a credentialed physiotherapist.</p>
              <Btn variant="primary" className="w-full mt-5 py-3" data-testid="send-referral-button" onClick={submit} disabled={saving}>
                {saving ? <Spinner className="w-4 h-4" /> : <>Issue Treatment Order</>}
              </Btn>
            </div>
          </Card>
        </div>
      )}

      {/* Footer nav */}
      <div className="flex justify-between items-center mt-7 sticky bottom-0 bg-[#F4F7F6] py-4 border-t border-slate-200">
        <Btn onClick={() => step === 0 ? nav("/referrals") : setStep(step - 1)} data-testid="wizard-back"><ArrowLeft size={16} /> {step === 0 ? "Cancel" : "Back"}</Btn>
        {step < 3 && (
          <Btn variant="primary" data-testid="wizard-next" onClick={() => goStep(step + 1)} disabled={step === 2 && (loadingAI || !plan || !planConfirmed)}>
            {step === 1 ? "Generate Treatment Plan" : "Continue"} <ArrowRight size={16} />
          </Btn>
        )}
      </div>

      {showCreate && <CreatePatientModal onClose={() => setShowCreate(false)} onCreated={(p) => { loadPatients(); setPatient(p); setShowCreate(false); toast.success("Patient created & selected"); }} />}
      {saveProtoOpen && plan && <SaveProtocolModal plan={plan} onClose={() => setSaveProtoOpen(false)} />}
    </>
  );
}

function EditableList({ label, items, onChange, testid }) {
  const setItem = (i, v) => onChange(items.map((x, j) => (j === i ? v : x)));
  return (
    <div className="mt-4">
      <div className="pq-label mb-1">{label}</div>
      {items.map((m, i) => (
        <div key={i} className="flex gap-2 items-center py-0.5">
          <CheckCircle2 size={14} className="text-teal-600 shrink-0" />
          <input className="pq-input py-1 flex-1 text-[13px]" value={m} onChange={(e) => setItem(i, e.target.value)} data-testid={`${testid}-${i}`} />
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-rose-400 hover:text-rose-600 shrink-0" data-testid={`${testid}-del-${i}`}><X size={14} /></button>
        </div>
      ))}
      <Btn size="sm" className="mt-1.5" onClick={() => onChange([...items, ""])} data-testid={`${testid}-add`}><Plus size={13} /> Add</Btn>
    </div>
  );
}

const PROTO_CATS = ["Knee & Hip", "Shoulder & Elbow", "Spine", "Neuro & Cardiac"];

function SaveProtocolModal({ plan, onClose }) {
  const [f, setF] = useState({ name: plan.name || "", category: "Knee & Hip", sessions: plan.sessions || 10, weeks: plan.weeks || 6, description: plan.description || "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Enter a protocol name");
    setSaving(true);
    try {
      await api.post("/protocols", { name: f.name.trim(), category: f.category, sessions: Number(f.sessions), weeks: Number(f.weeks), description: f.description });
      toast.success("Saved to your protocol library");
      onClose();
    } catch { toast.error("Could not save protocol"); setSaving(false); }
  };
  return (
    <Modal title="Save as New Protocol" onClose={onClose}>
      <p className="text-[12.5px] text-slate-500 mb-3 -mt-1">Save this tuned plan to your library so you can reuse it on future treatment orders.</p>
      <L label="Protocol Name"><input className="pq-input" data-testid="save-proto-name" value={f.name} onChange={set("name")} placeholder="e.g. ACL Phase I — 3x/week" /></L>
      <L label="Category"><select className="pq-input" data-testid="save-proto-category" value={f.category} onChange={set("category")}>{PROTO_CATS.map((c) => <option key={c}>{c}</option>)}</select></L>
      <div className="grid grid-cols-2 gap-3">
        <L label="Sessions"><input type="number" min="1" className="pq-input" data-testid="save-proto-sessions" value={f.sessions} onChange={set("sessions")} /></L>
        <L label="Duration (weeks)"><input type="number" min="1" className="pq-input" data-testid="save-proto-weeks" value={f.weeks} onChange={set("weeks")} /></L>
      </div>
      <L label="Description"><textarea className="pq-input min-h-[70px]" value={f.description} onChange={set("description")} placeholder="Protocol goals and structure..." /></L>
      <div className="flex justify-end gap-2 mt-4"><Btn onClick={onClose}>Cancel</Btn><Btn variant="primary" data-testid="save-proto-submit" onClick={save} disabled={saving}>{saving ? <Spinner className="w-4 h-4" /> : "Save Protocol"}</Btn></div>
    </Modal>
  );
}

function IssuedScreen({ order, nav }) {
  const pkg = order.package || {};
  const NEXT = [
    { icon: Bell, title: "Patient Activation", text: "The patient activates their care episode from the link sent by PranIQ." },
    { icon: Users, title: "Therapist Assigned", text: "We assign the most suitable credentialed physiotherapist." },
    { icon: CalendarClock, title: "Care Plan & Scheduling", text: "The therapist reviews and schedules the personalized care plan." },
    { icon: HeartPulse, title: "Care Start", text: "The patient begins treatment and progress tracking starts." },
  ];
  return (
    <div className="animate-fade-in max-w-4xl">
      <Card className="p-6 mb-5 text-center">
        <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3"><PartyPopper size={30} /></div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Treatment Order Issued</h1>
        <p className="text-slate-500 text-sm mt-1">A PranIQ care episode has been created and the patient has been notified.</p>
        <Badge tone="slate" className="mt-3">{order.ref_no}</Badge>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-5 items-start">
        <div className="space-y-5">
          <Card className="p-6" data-testid="issued-tracker">
            <h4 className="font-display font-semibold mb-4">Order Progress</h4>
            {(order.milestones || []).map((m) => (
              <div key={m.label} className="flex gap-3 py-3 border-b border-dashed border-slate-200 last:border-0">
                {m.state === "done" ? <CheckCircle2 className="text-teal-600 shrink-0" size={22} />
                  : m.state === "now" ? <div className="w-[22px] h-[22px] rounded-full border-[3px] border-teal-600 shrink-0" />
                  : <Circle className="text-slate-300 shrink-0" size={22} />}
                <div><div className={`font-semibold text-[13.5px] ${m.state === "todo" ? "text-slate-400" : ""}`}>{m.label}</div><div className="text-[12px] text-slate-500">{m.time}</div></div>
              </div>
            ))}
          </Card>
          <Card className="p-6">
            <h4 className="font-display font-semibold mb-4">What happens next</h4>
            <div className="space-y-3">
              {NEXT.map((n) => (
                <div key={n.title} className="flex gap-3">
                  <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0"><n.icon size={17} /></div>
                  <div><div className="font-semibold text-[13.5px]">{n.title}</div><div className="text-[12px] text-slate-500">{n.text}</div></div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="p-5">
            <h4 className="font-display font-semibold mb-3">Patient Summary</h4>
            <div className="flex items-center gap-3 mb-3"><Avatar name={order.patient_name} size={40} /><div><div className="font-semibold text-[13.5px]">{order.patient_name}</div><div className="text-[12px] text-slate-500">{order.patient_meta}</div></div></div>
            <Row k="Diagnosis" v={order.diagnosis_label} />
            <Row k="Package" v={pkg.package_name || pkg.name} />
            <Row k="Sessions" v={`${pkg.sessions} · ${pkg.weeks} weeks`} />
            <Row k="Physiotherapist" v={order.assigned_physio?.name || "Awaiting PranIQ assignment"} />
          </Card>
          <Card>
            <div className="px-5 py-4 border-b border-slate-100"><h4 className="font-display font-semibold">Actions</h4></div>
            <button onClick={() => nav(`/referrals/${order.id}`)} data-testid="issued-view-episode" className="w-full flex items-center gap-3 px-5 py-3 border-b border-slate-100 text-[13.5px] font-semibold hover:bg-slate-50 text-left"><FileText size={16} className="text-teal-600" /> View Care Episode</button>
            <button onClick={() => downloadOrderDoc(order)} data-testid="download-order-pdf" className="w-full flex items-center gap-3 px-5 py-3 border-b border-slate-100 text-[13.5px] font-semibold hover:bg-slate-50 text-left"><Download size={16} className="text-teal-600" /> Download Treatment Order</button>
            <button onClick={() => nav("/dashboard")} data-testid="issued-back-dashboard" className="w-full flex items-center gap-3 px-5 py-3 text-[13.5px] font-semibold hover:bg-slate-50 text-left"><ArrowLeft size={16} className="text-teal-600" /> Back to Dashboard</button>
          </Card>
          <Card className="p-5">
            <h4 className="font-display font-semibold mb-2 text-[13px]">Order Audit</h4>
            <Row k="Order ID" v={order.ref_no} />
            <Row k="Created By" v={order.created_by} />
            <Row k="Created On" v={order.created_label} />
            <Row k="Status" v={order.approval_status === "Pending" ? "Pending doctor approval" : "Sent"} />
          </Card>
        </div>
      </div>
    </div>
  );
}

function CreatePatientModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", age: "", gender: "Male", phone: "", email: "", hospital_id: "", facility: "", conditions: "", allergies: "No known drug allergies" });
  const [allergyOther, setAllergyOther] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    if (!f.name.trim()) return toast.error("Enter patient name");
    setSaving(true);
    try {
      const allergies = f.allergies === "Other (specify)" ? (allergyOther.trim() || "Other") : f.allergies;
      const { data } = await api.post("/patients", { ...f, allergies, age: f.age ? Number(f.age) : null }); onCreated(data);
    } catch { toast.error("Failed to create patient"); setSaving(false); }
  };
  return (
    <Modal title="Create New Patient" onClose={onClose}>
      <L label="Full Name"><input className="pq-input" data-testid="new-patient-name" value={f.name} onChange={set("name")} placeholder="e.g. Neha Kulkarni" /></L>
      <div className="grid grid-cols-2 gap-3">
        <L label="Age"><input type="number" className="pq-input" value={f.age} onChange={set("age")} /></L>
        <L label="Gender"><select className="pq-input" value={f.gender} onChange={set("gender")}><option>Female</option><option>Male</option><option>Other</option></select></L>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <L label="Phone"><input className="pq-input" value={f.phone} onChange={set("phone")} placeholder="10-digit mobile" /></L>
        <L label="Email"><input type="email" className="pq-input" value={f.email} onChange={set("email")} /></L>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <L label="Hospital ID"><input className="pq-input" data-testid="new-patient-hospital-id" value={f.hospital_id} onChange={set("hospital_id")} placeholder="e.g. HID-556677" /></L>
        <L label="Referring Facility"><input className="pq-input" value={f.facility} onChange={set("facility")} placeholder="Hospital / clinic" /></L>
      </div>
      <L label="Known Conditions"><input className="pq-input" value={f.conditions} onChange={set("conditions")} /></L>
      <L label="Allergies"><select className="pq-input" data-testid="new-patient-allergies" value={f.allergies} onChange={set("allergies")}>{ALLERGY_OPTIONS.map((a) => <option key={a}>{a}</option>)}</select></L>
      {f.allergies === "Other (specify)" && <L label="Specify allergy"><input className="pq-input" data-testid="new-patient-allergy-other" value={allergyOther} onChange={(e) => setAllergyOther(e.target.value)} placeholder="e.g. Codeine, shellfish" /></L>}
      <div className="flex justify-end gap-2 mt-4"><Btn onClick={onClose}>Cancel</Btn><Btn variant="primary" data-testid="submit-new-patient" onClick={create} disabled={saving}>{saving ? <Spinner className="w-4 h-4" /> : "Create Patient"}</Btn></div>
    </Modal>
  );
}

function PillGroup({ label, value, options, onChange }) {
  return (
    <div className="mb-4">
      <div className="pq-label mb-2">{label}</div>
      <div className="flex gap-2 flex-wrap">
        {options.map((o) => <button key={o} onClick={() => onChange(o)} className={`pq-pill ${value === o ? "pq-pill-active" : ""}`} data-testid={`pill-${o.replace(/[\s/]/g, "-")}`}>{o}</button>)}
      </div>
    </div>
  );
}
function TagInput({ label, tags, onChange, placeholder, testid }) {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onChange([...tags, v.trim()]); setV(""); } };
  return (
    <div className="mb-4">
      <div className="pq-label mb-2">{label}</div>
      <div className="flex gap-2 flex-wrap border border-slate-200 rounded-xl p-2 min-h-[44px] items-center">
        {tags.map((t, i) => (
          <span key={t} className="bg-slate-100 rounded-full pl-3 pr-2 py-1 text-[12.5px] font-medium flex items-center gap-1.5">{t}<button onClick={() => onChange(tags.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500"><X size={13} /></button></span>
        ))}
        <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} className="flex-1 min-w-[140px] outline-none text-sm bg-transparent py-1" data-testid={testid} />
      </div>
    </div>
  );
}
function Row({ k, v }) { return <div className="flex justify-between py-1.5 border-b border-dashed border-slate-100 last:border-0 text-[13px]"><span className="text-slate-500">{k}</span><span className="font-semibold">{v}</span></div>; }
function Section({ title, children }) { return <div className="mb-4"><div className="pq-label mb-1.5">{title}</div>{children}</div>; }
